import Swal from "sweetalert2";
import { useEffect, useMemo, useState } from "react";
import {
    hasPermissionForTag, hasPermissionForCancel, hasPermissionForModifyContrat,
    getCategorie, getAge, calculateDays, downloadPDF, tagContrat, cancelContrat, formatDate
} from './ContratFunctions.js';
import { FaFileContract, FaUser, FaPlane, FaFileDownload, FaTag, FaTrash, FaEdit, FaCheck } from 'react-icons/fa';
import { PrivateAxios } from '../../request/axios.js';
import { useEditablePersonnes } from "./useEditablePersonnes.js";
import { modifyContratPersonnes, modifyContratSouscripteur } from './ContratFunctions.js';

const modifiable_fields = [
    "nom", "nomEpouse", "prenom", "adresse", "codePostal", "ville", "telephone", "destination", "numCI", "profession",
];

function computeIsModifiable(created_date) {
    const diffMs = Date.now() - (created_date ? new Date(created_date).getTime() : Date.now());
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours < 48; // modifiable dans les 48h
}

const ModalContrat = ({
    selectedContrat = null,
    zone = "",
    userRoles = [],
    isLoading = false,
    setIsLoading = () => { },
    navigate = () => { },
    closeModal = () => { },
} = {}) => {
    // ---------- valeurs "safe" pour appeler les hooks même si selectedContrat est null
    const personnesSafe = selectedContrat?.data?.personnes ?? [];
    const sousInitial = selectedContrat?.data?.souscripteur ?? {};
    const contratId = selectedContrat?.id ?? null;

    // ---------- hook d’édition des PERSONNES (toujours appelé)
    const onSubmitPersonnes = async (payload) => {
        await modifyContratPersonnes(selectedContrat, zone, setIsLoading, navigate, payload);
    };
    const {
        drafts, editing, hasAnyEdit, hasChanges,
        startEdit, stopEdit, stopAll, changeField, validate,
    } = useEditablePersonnes(personnesSafe, onSubmitPersonnes);

    const startAll = () => personnesSafe.forEach(p => startEdit(p.id));

    // ---------- hooks d’édition du SOUSCRIPTEUR (toujours appelés)
    const [isEditingSous, setIsEditingSous] = useState(false);
    const [draftSouscripteur, setDraftSouscripteur] = useState(sousInitial);

    // reset du brouillon quand l’id de contrat ou les données initiales changent
    useEffect(() => {
        setDraftSouscripteur(sousInitial);
        setIsEditingSous(false);
    }, [contratId, sousInitial]);

    const hasSousChanges = useMemo(() => {
        return modifiable_fields.some((k) => {
            const a = sousInitial?.[k] ?? "";
            const b = draftSouscripteur?.[k] ?? "";
            const ta = typeof a === "string" ? a.trim() : a;
            const tb = typeof b === "string" ? b.trim() : b;
            return ta !== tb;
        });
    }, [sousInitial, draftSouscripteur]);

    const startEditSous = () => {
        setDraftSouscripteur(() =>
            modifiable_fields.reduce((acc, k) => ({ ...acc, [k]: sousInitial?.[k] ?? "" }), {})
        );
        setIsEditingSous(true);
    };

    const cancelEditSous = () => {
        setDraftSouscripteur(sousInitial);
        setIsEditingSous(false);
    };

    const changeSous = (field, value) => {
        setDraftSouscripteur(d => ({ ...d, [field]: value }));
    };

    // === [VALIDATION REQUIS + NUMÉRIQUE] ===
    // 1) Champs obligatoires
    const REQUIRED_FIELDS = new Set(["nom", "prenom", "adresse", "codePostal", "ville", "telephone", "destination", "numCI", "profession"]);

    // 2) Utilitaires
    const onlyDigits = (s = "") => s.replace(/\D/g, "");
    const isNonEmpty = (v) => String(v ?? "").trim().length > 0;

    // 3) Bornes
    const TEL_MAX = 6;
    const CP_LEN = 5;

    // 4) Handlers “nettoyeurs” pour les champs numériques
    const onTelChange = (e) =>
        changeSous("telephone", onlyDigits(e.target.value).slice(0, TEL_MAX));

    const onCPChange = (e) =>
        changeSous("codePostal", onlyDigits(e.target.value).slice(0, CP_LEN));

    // 5) Validation de format (et non-vide) pour les deux champs
    const telValid = isNonEmpty(draftSouscripteur.telephone) && new RegExp(`^\\d{${TEL_MAX}}$`).test(draftSouscripteur.telephone);
    const cpValid = isNonEmpty(draftSouscripteur.codePostal) && new RegExp(`^\\d{${CP_LEN}}$`).test(draftSouscripteur.codePostal);

    // 6) Validation des requis (non-vide “trim”) — HOOK: doit être avant tout return
    const requiredErrors = useMemo(() => {
        const errs = {};
        REQUIRED_FIELDS.forEach((field) => {
            const v = draftSouscripteur?.[field];
            errs[field] = !isNonEmpty(v); // true => erreur
        });
        // validations spécifiques
        errs.telephone = !telValid;
        errs.codePostal = !cpValid;
        return errs;
    }, [draftSouscripteur, telValid, cpValid]);

    // 7) Le formulaire est valide si aucun champ en erreur
    const formValid = Object.values(requiredErrors).every((isErr) => !isErr);

    const validateSous = async () => {
        if (!formValid) {
            await Swal.fire({ title: "Champs invalides", text: "Complète les champs requis.", icon: "warning" });
            return;
        }
        await modifyContratSouscripteur(
            selectedContrat, zone, setIsLoading, navigate,
            sousInitial, draftSouscripteur, modifiable_fields
        );
    };

    // ---------- return conditionnel après les hooks (OK avec la règle des hooks)
    if (!selectedContrat) return null;

    // ---------- variables pour le rendu (utilisées dans le JSX)
    const { souscripteur, personnes, contrat, reference } = selectedContrat.data;
    const created_date = formatDate(selectedContrat.created_date);
    const username = selectedContrat.username;
    const is_tagged = selectedContrat.is_tagged;
    const tagged_date = formatDate(selectedContrat.tagged_date);
    const is_canceled = selectedContrat.is_canceled;
    const canceled_date = formatDate(selectedContrat.canceled_date);

    const canTagContrat = hasPermissionForTag(zone, userRoles).reponse;
    const canCancelContrat = hasPermissionForCancel(zone, userRoles).reponse;
    const canChangeContrat = hasPermissionForModifyContrat(zone, userRoles).reponse;

    const isModifiable = computeIsModifiable(selectedContrat.created_date);

    // ---------- JSX
    return (
        <div className="relative p-4 space-y-6">
            {/* Loader */}
            {isLoading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75">
                    <div className="flex flex-col items-center justify-center w-full gap-4">
                        <div className="flex items-center justify-center text-4xl text-blue-400 border-8 border-gray-300 rounded-full w-28 h-28 animate-spin border-t-blue-400">
                            <svg viewBox="0 0 24 24" fill="currentColor" height="1em" width="1em" className="animate-ping">
                                <path d="M12.001 4.8c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624C13.666 10.618 15.027 12 18.001 12c3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C16.337 6.182 14.976 4.8 12.001 4.8zm-6 7.2c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624 1.177 1.194 2.538 2.576 5.512 2.576 3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C10.337 13.382 8.976 12 6.001 12z"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            )}

            {/* Header principal */}
            <h2 className="flex items-center justify-between pb-2 mb-4 text-3xl font-bold border-b-2 border-gray-300">
                <span className='text-gray-900'>Détails du Contrat</span>
                <div className='flex justify-end space-x-4'>
                    <span className={`text-sm font-semibold px-2 py-1 rounded ${is_tagged ? 'bg-green-100 text-success-green' : 'bg-red-100 text-danger-red'}`}>
                        {is_tagged ? `Inscrit en base le ${tagged_date}` : 'Non inscrit en base'}
                    </span>
                    {is_canceled && (
                        <span className='px-2 py-1 text-sm font-semibold text-white bg-red-800 rounded'>
                            Annulé le {canceled_date}
                        </span>
                    )}
                </div>
            </h2>
            <h3 className="flex items-center pb-2 mb-4 text-lg border-gray-300">
                <p><span className='font-bold text-gray-900'>Référence : </span> <span className='italic'>{reference}</span></p>
            </h3>

            {/* SOUSCRIPTEUR */}
            <div className="p-6 border-l-8 rounded-lg shadow-lg bg-blue-50 border-axa-blue-400">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <div className="p-3 mr-4 bg-blue-700 rounded-full">
                            <FaFileContract className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-axa-blue-400">Informations du Souscripteur</h3>
                    </div>

                    {canChangeContrat && isModifiable && (
                        isEditingSous ? (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={validateSous}
                                    disabled={!hasSousChanges || isLoading || !formValid}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
                                    type="button"
                                    title={
                                        !hasSousChanges ? "Aucun changement"
                                            : (!formValid ? "Veuillez corriger les champs en rouge"
                                                : "Valider les modifications")
                                    }
                                >
                                    <FaCheck className="w-4 h-4" />
                                    <span className="hidden sm:inline">Valider</span>
                                </button>
                                <button
                                    onClick={cancelEditSous}
                                    disabled={isLoading}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
                                    type="button"
                                >
                                    Annuler
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={startEditSous}
                                className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                                type="button"
                            >
                                <FaEdit className="w-4 h-4" />
                                <span className="hidden sm:inline">Modifier</span>
                            </button>
                        )
                    )}
                </div>

                {isEditingSous ? (
                    // --- MODE ÉDITION ---
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Nom</label>
                            <input
                                value={draftSouscripteur.nom ?? ""}
                                onChange={(e) => changeSous("nom", e.target.value)}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.nom ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder="Nom"
                                aria-invalid={requiredErrors.nom}
                            />
                            {requiredErrors.nom && <span className="mt-1 text-xs text-red-600">Champ requis.</span>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Nom d'épouse</label>
                            <input
                                value={draftSouscripteur.nomEpouse ?? ""}
                                onChange={(e) => changeSous("nomEpouse", e.target.value)}
                                className="px-2 py-1 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                placeholder="Nom d'épouse (optionnel)"
                            />
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Prénom</label>
                            <input
                                value={draftSouscripteur.prenom ?? ""}
                                onChange={(e) => changeSous("prenom", e.target.value)}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.prenom ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder="Prénom"
                                aria-invalid={requiredErrors.prenom}
                            />
                            {requiredErrors.prenom && <span className="mt-1 text-xs text-red-600">Champ requis.</span>}
                        </div>

                        <div className="flex flex-col sm:col-span-2">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Adresse</label>
                            <input
                                value={draftSouscripteur.adresse ?? ""}
                                onChange={(e) => changeSous("adresse", e.target.value)}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.adresse ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder="Adresse"
                                aria-invalid={requiredErrors.adresse}
                            />
                            {requiredErrors.adresse && <span className="mt-1 text-xs text-red-600">Champ requis.</span>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Code Postal</label>
                            <input
                                value={draftSouscripteur.codePostal ?? ""}
                                onChange={onCPChange}
                                inputMode="numeric"
                                pattern="\d*"
                                maxLength={CP_LEN}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.codePostal ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder={`Code Postal (${CP_LEN} chiffres)`}
                                aria-invalid={requiredErrors.codePostal}
                                onKeyDown={(e) => { const invalid = ["e", "E", "+", "-", "."]; if (invalid.includes(e.key)) e.preventDefault(); }}
                            />
                            {requiredErrors.codePostal && <span className="mt-1 text-xs text-red-600">Code postal : {CP_LEN} chiffres.</span>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Ville</label>
                            <input
                                value={draftSouscripteur.ville ?? ""}
                                onChange={(e) => changeSous("ville", e.target.value)}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.ville ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder="Ville"
                                aria-invalid={requiredErrors.ville}
                            />
                            {requiredErrors.ville && <span className="mt-1 text-xs text-red-600">Champ requis.</span>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Téléphone</label>
                            <input
                                value={draftSouscripteur.telephone ?? ""}
                                onChange={onTelChange}
                                inputMode="numeric"
                                pattern="\d*"
                                maxLength={TEL_MAX}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.telephone ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder={`Téléphone (${TEL_MAX} chiffres)`}
                                aria-invalid={requiredErrors.telephone}
                                onKeyDown={(e) => { const invalid = ["e", "E", "+", "-", "."]; if (invalid.includes(e.key)) e.preventDefault(); }}
                            />
                            {requiredErrors.telephone && <span className="mt-1 text-xs text-red-600">{TEL_MAX} chiffres.</span>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Destination</label>
                            <input
                                value={draftSouscripteur.destination ?? ""}
                                onChange={(e) => changeSous("destination", e.target.value)}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.destination ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder="Destination"
                                aria-invalid={requiredErrors.destination}
                            />
                            {requiredErrors.destination && <span className="mt-1 text-xs text-red-600">Champ requis.</span>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Numéro CI</label>
                            <input
                                value={draftSouscripteur.numCI ?? ""}
                                onChange={(e) => changeSous("numCI", e.target.value)}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.numCI ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder="Numéro CI"
                                aria-invalid={requiredErrors.numCI}
                            />
                            {requiredErrors.numCI && <span className="mt-1 text-xs text-red-600">Champ requis.</span>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 text-xs font-medium text-axa-blue-400">Profession</label>
                            <input
                                value={draftSouscripteur.profession ?? ""}
                                onChange={(e) => changeSous("profession", e.target.value)}
                                required
                                className={`px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 ${!requiredErrors.profession ? "border-slate-300 focus:ring-blue-300" : "border-red-400 focus:ring-red-300"
                                    }`}
                                placeholder="Profession"
                                aria-invalid={requiredErrors.profession}
                            />
                            {requiredErrors.profession && <span className="mt-1 text-xs text-red-600">Champ requis.</span>}
                        </div>
                    </div>
                ) : (
                    // --- MODE LECTURE SEULE ---
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <p><strong className="text-axa-blue-400">Genre : </strong>{souscripteur.genre ?? "—"}</p>
                        <p><strong className="text-axa-blue-400">Nom : </strong>{souscripteur.nomEpouse ? `${souscripteur.nom} ep ${souscripteur.nomEpouse}` : souscripteur.nom}</p>
                        <p><strong className="text-axa-blue-400">Prénom : </strong>{souscripteur.prenom}</p>
                        <p><strong className="text-axa-blue-400">Adresse : </strong>{souscripteur.adresse}</p>
                        <p><strong className="text-axa-blue-400">Code Postal : </strong>{souscripteur.codePostal}</p>
                        <p><strong className="text-axa-blue-400">Ville : </strong>{souscripteur.ville}</p>
                        <p><strong className="text-axa-blue-400">Téléphone : </strong>{souscripteur.telephone}</p>
                        <p><strong className="text-axa-blue-400">Destination : </strong>{souscripteur.destination}</p>
                        <p><strong className="text-axa-blue-400">Numéro CI : </strong>{souscripteur.numCI}</p>
                        <p><strong className="text-axa-blue-400">Profession : </strong>{souscripteur.profession}</p>
                        <p><strong className="text-axa-blue-400">Date de Départ : </strong>{formatDate(souscripteur.dateDepart)}</p>
                        <p><strong className="text-axa-blue-400">Date de Retour : </strong>{formatDate(souscripteur.dateRetour)}</p>
                        <p><strong className="text-axa-blue-400">Durée : </strong>{calculateDays(souscripteur.dateDepart, souscripteur.dateRetour)} jour(s)</p>
                    </div>
                )}
            </div>

            {/* PERSONNES AFFECTÉES */}
            <div className="p-6 border-l-8 rounded-lg shadow-lg bg-green-50 border-success-green">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <div className="p-3 mr-4 bg-green-700 rounded-full">
                            <FaUser className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-success-green">Personnes Affectées au Contrat</h3>
                    </div>

                    {canChangeContrat && isModifiable && (
                        hasAnyEdit ? (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={async () => { await validate(); }}
                                    disabled={!hasChanges || isLoading}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md shadow-sm text-white bg-green-700 hover:bg-green-800 disabled:opacity-60"
                                    type="button"
                                >
                                    <FaCheck className="w-4 h-4" />
                                    <span className="hidden sm:inline">Valider</span>
                                </button>

                                <button
                                    onClick={stopAll}
                                    disabled={isLoading}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
                                    type="button"
                                >
                                    Annuler
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={startAll}
                                className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md shadow-sm text-white bg-green-700 hover:bg-green-800"
                                type="button"
                            >
                                <FaEdit className="w-4 h-4" />
                                <span className="hidden sm:inline">Modifier</span>
                            </button>
                        )
                    )}
                </div>

                {personnes.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg shadow-lg">
                            <thead className="text-center bg-green-200 text-success-green">
                                <tr>
                                    <th className="px-4 py-2">Nom</th>
                                    <th className="px-4 py-2">Prénom</th>
                                    <th className="px-4 py-2">Date de Naissance</th>
                                    <th className="px-4 py-2">Catégorie</th>
                                    {souscripteur.optionForfait && (<th className="px-4 py-2">Éligible Forfait</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {personnes.map((p) => {
                                    const key = String(p.id);
                                    const rowDraft = drafts[key] ?? {};

                                    return (
                                        <tr key={p.id} className="text-center border-t hover:bg-green-100">
                                            <td className="px-4 py-2">
                                                {hasAnyEdit ? (
                                                    <input
                                                        value={rowDraft.nom ?? ""}
                                                        onChange={(e) => changeField(p.id, "nom", e.target.value)}
                                                        className="w-full px-2 py-1 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                                        placeholder="Nom"
                                                    />
                                                ) : (<span>{p.nom}</span>)}
                                            </td>
                                            <td className="px-4 py-2">
                                                {hasAnyEdit ? (
                                                    <input
                                                        value={rowDraft.prenom ?? ""}
                                                        onChange={(e) => changeField(p.id, "prenom", e.target.value)}
                                                        className="w-full px-2 py-1 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                                        placeholder="Prénom"
                                                    />
                                                ) : (<span>{p.prenom}</span>)}
                                            </td>
                                            <td className="px-4 py-2">{formatDate(p.dateNaissance)}</td>
                                            <td className="px-4 py-2">{getCategorie(p.dateNaissance, souscripteur.dateRetour)}</td>
                                            {souscripteur.optionForfait && (
                                                <td className="px-4 py-2">
                                                    {getAge(p.dateNaissance, souscripteur.dateRetour) < 70 ? "Oui" : "Non"}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-green-700">Erreur : Aucune personne associée au contrat.</p>
                )}
            </div>

            {/* DÉTAILS CONTRAT */}
            <div className="p-6 border-l-8 border-yellow-600 rounded-lg shadow-lg bg-yellow-50">
                <div className="flex items-center mb-4">
                    <div className="p-3 mr-4 rounded-full bg-warning-yellow">
                        <FaPlane className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-warning-yellow">Détails du Contrat</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <p><strong className="text-warning-yellow">Nombre de Jours : </strong>{contrat.nbJours}</p>
                    <p><strong className="text-warning-yellow">Tarif HT : </strong>{contrat.tarif.totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} XFP</p>
                    <p><strong className="text-warning-yellow">Tarif TTC : </strong>{contrat.tarif.totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} XFP</p>
                    <p><strong className="text-warning-yellow">Tarification : </strong>{souscripteur.optionFamille ? 'Solo/Duo/Famille' : 'Groupe'}</p>
                    <p><strong className="text-warning-yellow">Option Forfait : </strong>{souscripteur.optionForfait ? 'Oui' : 'Non'}</p>
                    {contrat.tarif.remiseGroup && (<p><strong className="text-warning-yellow">Remise groupe : </strong>{Math.round(contrat.tarif.remiseGroup * 100)} %</p>)}
                    {contrat.tarif.remise && (<p><strong className="text-warning-yellow">Remise salarié : </strong>{Math.round(contrat.tarif.remise * 100)} %</p>)}
                    {contrat.tarif.remisePartenaire && (<p><strong className="text-warning-yellow">Remise partenaire : </strong>{Math.round(contrat.tarif.remisePartenaire * 100)} %</p>)}
                    {contrat.tarif.remiseExceptionnelle && (<p><strong className="text-warning-yellow">Remise exceptionnelle : </strong>{Math.round(contrat.tarif.remiseExceptionnelle * 100)} %</p>)}
                    <p><strong className="text-warning-yellow">Version : </strong>{contrat.version}</p>
                    <p><strong className="text-warning-yellow">Enregistré le : </strong>{created_date}</p>
                    <p><strong className="text-warning-yellow">Enregistré par : </strong>{username}</p>
                </div>
            </div>

            {/* ACTIONS FOOTER */}
            <div className="flex flex-col gap-2 mt-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => downloadPDF(selectedContrat.id, setIsLoading, PrivateAxios)}
                        className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md shadow-sm text-white bg-axa-blue-400 hover:bg-hover-axa-blue transition focus:outline-none focus:ring-2 focus:ring-axa-blue-300 focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={isLoading}
                        type="button"
                        aria-label="Télécharger le PDF"
                    >
                        <FaFileDownload className="w-4 h-4" />
                        <span className="hidden sm:inline">Télécharger PDF</span>
                    </button>

                    {canTagContrat && !is_tagged && (
                        <button
                            onClick={() => tagContrat(selectedContrat, zone, setIsLoading, navigate)}
                            className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md shadow-sm text-white bg-success-green hover:bg-hover-axa-green transition focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={isLoading}
                            type="button"
                            aria-label="Tagger comme saisi"
                        >
                            <FaTag className="w-4 h-4" />
                            <span className="hidden sm:inline">Tagger comme saisi</span>
                        </button>
                    )}

                    {canCancelContrat && !is_canceled && (
                        <button
                            onClick={() => cancelContrat(selectedContrat, zone, setIsLoading, navigate)}
                            className="inline-flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md shadow-sm text-white bg-danger-red hover:bg-red-700 transition focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={isLoading}
                            type="button"
                            aria-label="Annuler le contrat"
                        >
                            <FaTrash className="w-4 h-4" />
                            <span className="hidden sm:inline">Annuler</span>
                        </button>
                    )}
                </div>

                <button
                    onClick={closeModal}
                    className="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed sm:ml-auto"
                    disabled={isLoading}
                    type="button"
                >
                    Fermer
                </button>
            </div>
        </div>
    );
};

export default ModalContrat;
